/**
 * The ONE sample-data module for WagePro's UI.
 *
 * Screens must never import this file. They read data through the hooks in
 * `src/data/use_*.ts`, so pointing WagePro at the real payroll API later is a
 * change to those hooks and nothing else.
 *
 * Scenario: Ceylon Brightways (Pvt) Ltd, April 2026 pay cycle, mid-cycle.
 */

import type {
  DashboardSummary,
  NotificationItem,
  OperationsSettings,
  OrganisationSettings,
  PayRun,
  SessionUser,
  UpdateRequest,
} from './types';

/**
 * Fixed "current time" for the sample data. Relative labels ("10 minutes ago")
 * are derived from this rather than the wall clock so the mock UI is
 * deterministic — the real hooks will use `Date.now()` once wired.
 */
export const MOCK_NOW = '2026-04-12T09:50:00Z';

export const MOCK_CURRENCY = 'LKR' as const;

export const MOCK_SESSION: SessionUser = {
  name: 'Dilshan Rajapaksa',
  initials: 'DR',
  tint: 'blue',
  role: 'admin',
  company_name: 'Ceylon Brightways (Pvt) Ltd',
};

const APRIL_2026_LINE_ITEMS = [
  { employee_id: 'e1', employee_name: 'Nadeesha Perera', gross: 18_500_000 },
  { employee_id: 'e2', employee_name: 'Ruwan Fernando', gross: 14_500_000 },
  {
    employee_id: 'e3',
    employee_name: 'Ashen Wickramasinghe',
    gross: 21_000_000,
  },
  { employee_id: 'e4', employee_name: 'Chamari Gunawardena', gross: 9_500_000 },
  { employee_id: 'e5', employee_name: 'Dilshan Rajapaksa', gross: 17_500_000 },
].map((line) => ({
  ...line,
  // Allowances are a MEMO breakdown of gross, never an amount on top of it
  // (#24395) — the real engine folds earnings pay items into
  // `pay_run_lines.gross_pay_cents` before deductions are applied, so these
  // fixtures fold them in the same way and `line_net` is gross - deductions.
  gross: line.gross + 2_000_000,
  allowances: 2_000_000,
  deductions: 2_600_000,
}));

export const MOCK_PAY_RUNS: PayRun[] = [
  {
    id: 'r1',
    created_at: '2026-04-01T09:12:00Z',
    period: { year: 2026, month: 4 },
    currency: MOCK_CURRENCY,
    // Gross is inclusive of `total_allowances` (a memo breakdown), and
    // reconciles: gross - statutory - other === net_payable (#24395).
    gross_wages: 569_400_000,
    total_allowances: 51_000_000,
    statutory_deductions: 70_140_000,
    other_deductions: 3_400_000,
    net_payable: 495_860_000,
    employee_count: 34,
    status: 'in_review',
    pay_date: '2026-04-30',
    period_start: '2026-04-01',
    period_end: '2026-04-30',
    filing_due_on: '2026-04-30',
    line_items: APRIL_2026_LINE_ITEMS,
    statutory: {
      epf_employee: 41_200_000,
      epf_employer: 61_800_000,
      etf_employer: 15_450_000,
      apit_withheld: 28_940_000,
      epf_employee_rate: 800,
      epf_employer_rate: 1_200,
      etf_employer_rate: 300,
    },
    exceptions: [
      {
        id: 'ex1',
        kind: 'bank_details_incomplete',
        employee_name: 'Sanduni Jayasuriya',
        acknowledged: false,
      },
      {
        id: 'ex2',
        kind: 'new_joiner_unconfigured',
        employee_name: null,
        acknowledged: false,
      },
    ],
    approval: {
      preparer_name: 'Dilshan Rajapaksa',
      preparer_state: 'submitted',
      approver_name: 'Kasun Bandara',
      approver_title: 'Finance Manager',
      approver_state: 'not_submitted',
    },
  },
  {
    id: 'r2',
    created_at: '2026-03-01T09:00:00Z',
    period: { year: 2026, month: 3 },
    currency: MOCK_CURRENCY,
    gross_wages: 555_300_000,
    total_allowances: 49_800_000,
    statutory_deductions: 68_580_000,
    other_deductions: 3_300_000,
    net_payable: 483_420_000,
    employee_count: 33,
    status: 'paid',
    pay_date: '2026-03-31',
    period_start: '2026-03-01',
    period_end: '2026-03-31',
    filing_due_on: '2026-03-31',
    line_items: [],
    statutory: {
      epf_employee: 40_160_000,
      epf_employer: 60_240_000,
      etf_employer: 15_060_000,
      apit_withheld: 28_420_000,
      epf_employee_rate: 800,
      epf_employer_rate: 1_200,
      etf_employer_rate: 300,
    },
    exceptions: [],
    approval: {
      preparer_name: 'Dilshan Rajapaksa',
      preparer_state: 'submitted',
      approver_name: 'Kasun Bandara',
      approver_title: 'Finance Manager',
      approver_state: 'approved',
    },
  },
  {
    id: 'r3',
    created_at: '2026-02-01T09:00:00Z',
    period: { year: 2026, month: 2 },
    currency: MOCK_CURRENCY,
    gross_wages: 550_300_000,
    total_allowances: 49_200_000,
    statutory_deductions: 67_900_000,
    other_deductions: 3_300_000,
    net_payable: 479_100_000,
    employee_count: 33,
    status: 'paid',
    pay_date: '2026-02-28',
    period_start: '2026-02-01',
    period_end: '2026-02-28',
    filing_due_on: '2026-02-28',
    line_items: [],
    statutory: {
      epf_employee: 39_840_000,
      epf_employer: 59_760_000,
      etf_employer: 14_940_000,
      apit_withheld: 28_060_000,
      epf_employee_rate: 800,
      epf_employer_rate: 1_200,
      etf_employer_rate: 300,
    },
    exceptions: [],
    approval: {
      preparer_name: 'Dilshan Rajapaksa',
      preparer_state: 'submitted',
      approver_name: 'Kasun Bandara',
      approver_title: 'Finance Manager',
      approver_state: 'approved',
    },
  },
  {
    id: 'r4',
    created_at: '2026-01-01T09:00:00Z',
    period: { year: 2026, month: 1 },
    currency: MOCK_CURRENCY,
    gross_wages: 542_200_000,
    total_allowances: 48_600_000,
    statutory_deductions: 67_470_000,
    other_deductions: 3_200_000,
    net_payable: 471_530_000,
    employee_count: 32,
    status: 'filed',
    pay_date: '2026-01-31',
    period_start: '2026-01-01',
    period_end: '2026-01-31',
    filing_due_on: '2026-01-31',
    line_items: [],
    statutory: {
      epf_employee: 39_200_000,
      epf_employer: 58_800_000,
      etf_employer: 14_700_000,
      apit_withheld: 27_570_000,
      epf_employee_rate: 800,
      epf_employer_rate: 1_200,
      etf_employer_rate: 300,
    },
    exceptions: [],
    approval: {
      preparer_name: 'Dilshan Rajapaksa',
      preparer_state: 'submitted',
      approver_name: 'Kasun Bandara',
      approver_title: 'Finance Manager',
      approver_state: 'approved',
    },
  },
];

export const MOCK_UPDATE_REQUESTS: UpdateRequest[] = [
  {
    id: 'ur1',
    kind: 'bank_account',
    requester_name: 'Nadeesha Perera',
    submitted_on: '2026-04-03',
    status: 'in_review',
    old_value: 'HNB •••• 2210',
    new_value: 'Sampath •••• 7742',
  },
  {
    id: 'ur2',
    kind: 'address',
    requester_name: 'Ashen Wickramasinghe',
    submitted_on: '2026-04-05',
    status: 'in_review',
    old_value: '14 Galle Rd, Colombo 6',
    new_value: '22 Galle Rd, Colombo 6',
  },
  {
    id: 'ur3',
    kind: 'dependent',
    requester_name: 'Kasun Bandara',
    submitted_on: '2026-04-07',
    status: 'approved',
    old_value: '1',
    new_value: '2',
  },
  {
    id: 'ur4',
    kind: 'emergency_contact',
    requester_name: 'Sanduni Jayasuriya',
    submitted_on: '2026-04-09',
    status: 'in_review',
    old_value: 'M. Jayasuriya — 077•••1122',
    new_value: 'R. Jayasuriya — 071•••4456',
  },
  {
    id: 'ur5',
    kind: 'name_correction',
    requester_name: 'Ishara Senanayake',
    submitted_on: '2026-04-11',
    status: 'rejected',
    old_value: 'Ishara Senanayaka',
    new_value: 'Ishara Senanayake',
  },
];

export const MOCK_ORGANISATION_SETTINGS: OrganisationSettings = {
  legal_entity: 'Ceylon Brightways (Pvt) Ltd',
  fiscal_year_start_month: 4,
  base_currency: MOCK_CURRENCY,
};

/**
 * PERMANENTLY mock -- no backend concept exists for the Settings screen's
 * "Operations" tab: no `entities` columns, no procedure, nothing to read or
 * write. Unlike `organisation` (real as of C6/C11, #21937/#22333) and
 * `payroll` (real as of #22729/#22730 -- see `use_payroll_settings.ts`'s
 * `usePayrollSettings()`, which replaced this fixture's former sibling
 * `MOCK_PAYROLL_SETTINGS`), Operations is explicitly OUT OF SCOPE for the
 * `wagepro-production-auth` epic -- see
 * `docs/epics/wagepro-production-auth.md`'s Non-goals section. Do not invent
 * a backend for it; `useSettings()` (`use_settings.ts`) returns this fixture
 * verbatim and documents the same "intentionally not-yet-wired" note at its
 * own return statement.
 */
export const MOCK_OPERATIONS_SETTINGS: OperationsSettings = {
  pay_cycle: 'monthly_last_working_day',
  holiday_calendar: 'Sri Lanka (public + mercantile)',
  notifications: ['email', 'in_app'],
};

export const MOCK_NOTIFICATIONS: NotificationItem[] = [
  {
    id: 'n1',
    kind: 'expenses_awaiting_approval',
    at: '2026-04-12T09:40:00Z',
    count: 2,
  },
  { id: 'n2', kind: 'filing_due', at: '2026-04-12T08:50:00Z', days: 3 },
  {
    id: 'n3',
    kind: 'bank_update_requested',
    at: '2026-04-12T07:50:00Z',
    actor: 'Nadeesha Perera',
  },
];

/**
 * Illustrative "joiners missing bank details" count shown only while
 * `payroll.listEmployees` hasn't resolved yet (see
 * `wagepro_store_dashboard.ts`'s `deriveJoinersMissingBankCount`, C5,
 * #21936). Hoisted to a named constant (rather than an inline magic `2` in
 * `use_dashboard.ts`) so it reads as an intentional mock value, same as
 * every other `MOCK_*` export here.
 */
export const MOCK_JOINERS_MISSING_BANK_COUNT = 2;

export const MOCK_DASHBOARD: DashboardSummary = {
  company_name: 'Ceylon Brightways (Pvt) Ltd',
  greeting_name: 'Dilshan',
  period: { year: 2026, month: 4 },
  headcount: 34,
  headcount_delta: 2,
  gross_payroll: 515_000_000,
  gross_payroll_delta_bp: 260,
  compliance_alerts: 1,
  statutory_deadline: {
    kind: 'upcoming',
    period: { year: 2026, month: 4 },
    amount_due: 70_140_000,
    filed_via: 'IRD RAMIS',
    owner: 'Dilshan Rajapaksa',
    due_on: '2026-04-30',
    days_remaining: 3,
    next: null,
  },
  activity: [
    {
      id: 'ev1',
      kind: 'exceptions_acknowledged',
      at: '2026-04-12T09:40:00Z',
      actor: 'Dilshan Rajapaksa',
      count: 2,
    },
    {
      id: 'ev2',
      kind: 'pay_run_created',
      at: '2026-04-12T09:12:00Z',
      period: { year: 2026, month: 4 },
    },
    {
      id: 'ev3',
      kind: 'pay_run_approved',
      at: '2026-04-11T14:05:00Z',
      actor: 'Kasun Bandara',
      period: { year: 2026, month: 3 },
    },
    {
      id: 'ev4',
      kind: 'expense_submitted',
      at: '2026-04-11T11:20:00Z',
      actor: 'Ashen Wickramasinghe',
    },
  ],
  // No document-expiry concept exists anywhere in the schema (C5, #21936) —
  // retired the previously-fabricated demo array rather than keep shipping
  // a "feature" no backend will ever back without a product decision.
  // `ExpiringDocumentsCard` renders an `EmptyState` for the empty list.
  expiring_documents: [],
};
